import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useSitesStore } from '@/stores/sites';
import { usePreferencesStore } from '@/stores/preferences';
import { analyticsService } from '@/services/analytics';
import { Card, Button } from '@/components/UI';
import { PeriodSelector, FilterBar } from '@/components/Dashboard';
import type { AnalyticsPeriod, DateRange, Filters, FilterKey, EventLogEntry } from '@/types';

function ClassificationBadge({ value }: { value: string }) {
  if (value === 'crawler') {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">Crawler</span>;
  }
  if (value === 'bot') {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700">Bot</span>;
  }
  if (value === 'suspicious') {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-50 text-yellow-700">Suspicious</span>;
  }
  return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700">Human</span>;
}

export function SiteEventLog() {
  const { siteId } = useParams<{ siteId: string }>();
  const { currentSite, fetchSite } = useSitesStore();
  const { fetchPreferences } = usePreferencesStore();

  const [events, setEvents] = useState<EventLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [period, setPeriod] = useState<AnalyticsPeriod>('30d');
  const [customDateRange, setCustomDateRange] = useState<DateRange | null>(null);
  const [filters, setFilters] = useState<Filters>({});
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [classification, setClassification] = useState('');
  const limit = 50;

  const searchTimerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    fetchPreferences();
  }, []);

  useEffect(() => {
    if (siteId) {
      fetchSite(siteId);
    }
  }, [siteId]);

  // Debounce search input
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(search);
      setOffset(0);
    }, 300);
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, [search]);

  // Fetch events when params change
  const fetchEvents = useCallback(async () => {
    if (!siteId) return;
    setIsLoading(true);
    try {
      const data = await analyticsService.getEventLog(
        siteId, period,
        period === 'custom' ? customDateRange : null,
        filters, limit, offset,
        debouncedSearch || undefined,
        classification || undefined,
      );
      setEvents(data.events);
      setTotal(data.total);
    } catch (err) {
      console.error('Failed to fetch event log:', err);
      setEvents([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  }, [siteId, period, customDateRange, filters, offset, debouncedSearch, classification]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleFilter = useCallback((key: FilterKey, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setOffset(0);
  }, []);

  const handleRemoveFilter = useCallback((key: FilterKey) => {
    setFilters(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setOffset(0);
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters({});
    setOffset(0);
  }, []);

  const handlePeriodChange = useCallback((p: AnalyticsPeriod) => {
    setPeriod(p);
    setCustomDateRange(null);
    setOffset(0);
  }, []);

  const handleCustomDateRange = useCallback((range: DateRange) => {
    setPeriod('custom');
    setCustomDateRange(range);
    setOffset(0);
  }, []);

  const handleExportCsv = useCallback(() => {
    if (!siteId) return;
    const url = analyticsService.getEventLogExportUrl(
      siteId, period,
      period === 'custom' ? customDateRange : null,
      filters,
      debouncedSearch || undefined,
      classification || undefined,
    );
    window.open(url, '_blank');
  }, [siteId, period, customDateRange, filters, debouncedSearch, classification]);

  const showingStart = total === 0 ? 0 : offset + 1;
  const showingEnd = Math.min(offset + limit, total);

  if (!currentSite && !isLoading) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900">Site not found</h2>
        <Link to="/dashboard" className="mt-4 inline-block">
          <Button variant="secondary">Back to dashboard</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Link to={`/sites/${siteId}`} className="text-gray-500 hover:text-gray-700">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {currentSite?.name || 'Loading...'}
            </h1>
            <p className="text-gray-600">Event Log</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link to={`/sites/${siteId}`}>
            <Button variant="secondary" size="sm">Dashboard</Button>
          </Link>
          <Link to={`/sites/${siteId}/settings`}>
            <Button variant="secondary" size="sm">Settings</Button>
          </Link>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 flex-wrap">
        <PeriodSelector
          value={period}
          onChange={handlePeriodChange}
          customDateRange={customDateRange}
          onCustomDateRange={handleCustomDateRange}
        />
        <div className="relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search path, UA, referrer..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm w-64 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
        <select
          value={classification}
          onChange={(e) => { setClassification(e.target.value); setOffset(0); }}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="">All Classifications</option>
          <option value="human">Human</option>
          <option value="crawler">Crawler</option>
          <option value="bot">Bot</option>
          <option value="suspicious">Suspicious</option>
        </select>
        <Button variant="secondary" size="sm" onClick={handleExportCsv}>
          <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Export CSV
        </Button>
      </div>

      {/* Filter Bar */}
      <FilterBar filters={filters} onRemove={handleRemoveFilter} onClear={handleClearFilters} />

      {/* Events Table */}
      <Card>
        {isLoading ? (
          <div className="animate-pulse p-4 space-y-3">
            <div className="h-8 bg-gray-200 rounded" />
            <div className="h-8 bg-gray-100 rounded" />
            <div className="h-8 bg-gray-100 rounded" />
            <div className="h-8 bg-gray-100 rounded" />
            <div className="h-8 bg-gray-100 rounded" />
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p>No events found for this period.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Path</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Class</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IP</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Browser</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Country</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {events.map((event) => (
                  <tr key={event.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                      {new Date(event.created_at).toLocaleTimeString()}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 max-w-[200px] truncate">
                      <button
                        onClick={() => handleFilter('page_path', event.page_path)}
                        className="hover:text-primary-600 hover:underline text-left"
                        title={event.page_path}
                      >
                        {event.page_path}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                      {event.event_type}
                    </td>
                    <td className="px-4 py-3 text-sm whitespace-nowrap">
                      <ClassificationBadge value={event.classification} />
                      {event.bot_name && (
                        <span className="ml-2 text-xs text-gray-500">{event.bot_name}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap font-mono text-xs">
                      {event.ip_address
                        ? event.ip_address.includes(':')
                          ? event.ip_address.substring(0, 19) + (event.ip_address.length > 19 ? '...' : '')
                          : event.ip_address
                        : '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                      <button
                        onClick={() => event.referrer_host && handleFilter('referrer_host', event.referrer_host)}
                        className="hover:text-primary-600 hover:underline text-left"
                      >
                        {event.referrer_host || 'Direct'}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                      <button
                        onClick={() => event.browser && handleFilter('browser', event.browser)}
                        className="hover:text-primary-600 hover:underline text-left"
                      >
                        {event.browser ? `${event.browser}/${event.os || ''}` : '-'}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                      <button
                        onClick={() => event.country && handleFilter('country', event.country)}
                        className="hover:text-primary-600 hover:underline text-left"
                      >
                        {event.country || '-'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {total > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
            <Button
              variant="secondary"
              size="sm"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - limit))}
            >
              Previous
            </Button>
            <span className="text-sm text-gray-600">
              Showing {showingStart.toLocaleString()}-{showingEnd.toLocaleString()} of {total.toLocaleString()}
            </span>
            <Button
              variant="secondary"
              size="sm"
              disabled={offset + limit >= total}
              onClick={() => setOffset(offset + limit)}
            >
              Next
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}

export default SiteEventLog;
